/* eslint-disable max-lines */
import moment from 'moment';
import { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Modal,
  Row,
  Card,
} from 'reactstrap';
import ReactDatetime from 'react-datetime';
import { toast } from 'react-toastify';
import EmployeeTerminate from '../../../services/EmployeeTerminate';
import Messaging from '../../../services/Messaging';
import filterEligibleSupervisors from '../../../helpers/filterEligibleSupervisors';
import DirectReportsReassignment from './DirectReportsReassignment';
import { toLocaleFormatSlash } from '../../../helpers/dateFormat';
import PreloadRecordsTable from './PreloadRecordsTable';

interface TerminatedEmployeeFormV2Props {
  isOpen: boolean;
  toggle: () => void;
  employee: any;
  schoolId: string;
  token: string;
  onSuccess: () => void;
}

const TerminatedEmployeeFormV2 = ({
  isOpen,
  toggle,
  employee,
  schoolId,
  token,
  onSuccess,
}: TerminatedEmployeeFormV2Props) => {
  const [loading, setLoading] = useState(false);
  const [terminationDate, setTerminationDate] = useState(moment().format('MM/DD/YYYY'));
  const [terminationCategory, setTerminationCategory] = useState('');
  const [willBeRehired, setWillBeRehired] = useState<boolean | null>(null);
  const [terminationReason, setTerminationReason] = useState('');
  const [terminationUserEmail, setTerminationUserEmail] = useState('');
  const [classroomObservation, setClassroomObservation] = useState('');
  const [thirtyDayCheckIn, setThirtyDayCheckIn] = useState('');
  const [oneOnOnesRecords, setOneOnOnesRecords] = useState<any[]>([]);
  const [feedbacksRecords, setFeedbacksRecords] = useState<any[]>([]);
  const [feedbackImprovement, setFeedbackImprovement] = useState('');
  const [improvementsRecords, setImprovementsRecords] = useState<any[]>([]);
  const [performanceImprovementPlanImprovement, setPerformanceImprovementPlanImprovement] = useState('');
  const [verbalWarningsRecords, setVerbalWarningsRecords] = useState<any[]>([]);
  const [verbalWarningImprovement, setVerbalWarningImprovement] = useState('');
  const [disciplinaryActionsRecords, setDisciplinaryActionsRecords] = useState<any[]>([]);
  const [disciplinaryActionNoticeImprovement, setDisciplinaryActionNoticeImprovement] = useState('');
  const [backUpPlan, setBackUpPlan] = useState('');
  const [directReports, setDirectReports] = useState<any[]>([]);
  const [supervisors, setSupervisors] = useState<any[]>([]);
  const [newSupervisorId, setNewSupervisorId] = useState('');

  useEffect(() => {
    if (isOpen && employee?.id) {
      loadPreloadData();
      setTerminationUserEmail(employee?.user?.email || '');
      resetForm();
    }
  }, [isOpen, employee]);

  const loadPreloadData = async () => {
    if (!employee?.id) return;
    try {
      const data = await EmployeeTerminate(token).getPreloadData(employee.id);
      if (data.oneOnOnesRecords) {
        setOneOnOnesRecords(data.oneOnOnesRecords);
      }
      if (data.feedbacksRecords) {
        setFeedbacksRecords(data.feedbacksRecords);
      }
      if (data.improvementsRecords) {
        setImprovementsRecords(data.improvementsRecords);
      }
      if (data.verbalWarningsRecords) {
        setVerbalWarningsRecords(data.verbalWarningsRecords);
      }
      if (data.disciplinaryActionsRecords) {
        setDisciplinaryActionsRecords(data.disciplinaryActionsRecords);
      }
      const reports = data.directReports || [];
      setDirectReports(reports);
      if (reports.length) {
        await loadSupervisors();
      }
    } catch (e: any) {
      console.error('Error loading preload data:', e);
    }
  };

  const loadSupervisors = async () => {
    try {
      const result = await Messaging(token).listSelectStaff(schoolId);
      setSupervisors(filterEligibleSupervisors(result.data).filter((sup: any) => sup.userId !== employee?.user?.id));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to load supervisors.');
    }
  };

  const resetForm = () => {
    setTerminationDate(moment().format('MM/DD/YYYY'));
    setTerminationCategory('');
    setWillBeRehired(null);
    setTerminationReason('');
    setClassroomObservation('');
    setThirtyDayCheckIn('');
    setOneOnOnesRecords([]);
    setFeedbacksRecords([]);
    setFeedbackImprovement('');
    setImprovementsRecords([]);
    setPerformanceImprovementPlanImprovement('');
    setVerbalWarningsRecords([]);
    setVerbalWarningImprovement('');
    setDisciplinaryActionsRecords([]);
    setDisciplinaryActionNoticeImprovement('');
    setBackUpPlan('');
    setDirectReports([]);
    setSupervisors([]);
    setNewSupervisorId('');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event?.preventDefault();
    setLoading(true);

    try {
      const feedbackText =
        feedbacksRecords.length > 0
          ? `${feedbacksRecords.length} feedback(s). Policies: ${feedbacksRecords
              .flatMap((r) => r.policyViolations?.map((p: any) => p?.name) || [])
              .filter((v, i, a) => a.indexOf(v) === i)
              .join(', ')}`
          : '';
      const improvementText =
        improvementsRecords.length > 0
          ? `${improvementsRecords.length} Performance Improvement Plan(s). Policies: ${improvementsRecords
              .flatMap((r) => r.policyViolations?.map((p: any) => p?.name) || [])
              .filter((v, i, a) => a.indexOf(v) === i)
              .join(', ')}`
          : '';
      const oneOnOneText =
        oneOnOnesRecords.length > 0
          ? `${oneOnOnesRecords.length} one on one(s). Policies: ${oneOnOnesRecords
              .flatMap((r) => r.policyViolations?.map((p: any) => p?.name) || [])
              .filter((v, i, a) => a.indexOf(v) === i)
              .join(', ')}`
          : '';
      const verbalWarningText = verbalWarningsRecords.length > 0 ? verbalWarningsRecords.length.toString() : '';
      const disciplinaryActionText =
        disciplinaryActionsRecords.length > 0
          ? `${disciplinaryActionsRecords.length} Disciplinary Action Notice(s). Policies: ${disciplinaryActionsRecords
              .flatMap((r) => r.policyViolations?.map((p: any) => p?.name) || [])
              .filter((v, i, a) => a.indexOf(v) === i)
              .join(', ')}`
          : '';

      const data: any = {
        employeeId: employee?.id,
        schoolId,
        lastWorkDay: terminationDate,
        category: terminationCategory,
        willBeRehired,
        reason: terminationReason,
        email: terminationUserEmail,
        classroomObservation,
        thirtyDayCheckIn,
        oneOnOne: oneOnOneText,
        feedback: feedbackText,
        feedbackImprovement,
        performanceImprovementPlan: improvementText,
        performanceImprovementPlanImprovement,
        verbalWarning: verbalWarningText,
        verbalWarningImprovement,
        disciplinaryActionNotice: disciplinaryActionText,
        disciplinaryActionNoticeImprovement,
        backUpPlan,
      };
      if (directReports.length) {
        data.newSupervisorId = newSupervisorId;
      }

      await EmployeeTerminate(token).create(data);
      toast.success('Termination request submitted for approval.');
      resetForm();
      toggle();
      onSuccess();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Request error');
    } finally {
      setLoading(false);
    }
  };

  if (!employee) return null;

  return (
    <Modal className="modal-dialog-centered" isOpen={isOpen} toggle={toggle} size={'xl'}>
      <div className="modal-header">
        <h4 className="modal-title">Approval Questions: Termination Request</h4>
        <div onClick={toggle} className="d-flex p-1 cursor-pointer">
          <i className="fas fa-times" />
        </div>
      </div>
      <div className="modal-body">
        <div className="px-2">
          <Form onSubmit={handleSubmit}>
            <Card style={{ padding: '20px', background: '#f8f9fa', marginBottom: '20px' }}>
              <h5>Employee Information</h5>
              <Row className="mb-2">
                <Col lg="6">
                  <strong>Full Name:</strong> {employee?.user?.firstName} {employee?.user?.lastName}
                </Col>
                <Col lg="6">
                  <strong>Start Date:</strong> {employee?.startDate ? toLocaleFormatSlash(employee.startDate) : '-'}
                </Col>
              </Row>
              <Row className="mb-2">
                <Col lg="6">
                  <strong>Job Title:</strong> {employee?.title?.name || '-'}
                </Col>
                <Col lg="6">
                  <strong>Department:</strong> {employee?.department?.name || '-'}
                </Col>
              </Row>
            </Card>

            <Row className="d-flex align-items-center mt-4">
              <Col lg="4">
                <label className="form-control-label">Requested Termination Date *</label>
              </Col>
              <Col lg="8">
                <FormGroup className="custom-datepicker">
                  <InputGroup className="input-group-alternative">
                    <ReactDatetime
                      inputProps={{
                        placeholder: 'Termination Date',
                      }}
                      onChange={(newValue) => {
                        setTerminationDate(moment(newValue).format('MM/DD/yyyy'));
                      }}
                      timeFormat={false}
                      value={terminationDate}
                      dateFormat={'MM/DD/yyyy'}
                      closeOnSelect={true}
                    />
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>
                        <i className="ni ni-calendar-grid-58" />
                      </InputGroupText>
                    </InputGroupAddon>
                  </InputGroup>
                </FormGroup>
              </Col>
            </Row>

            <Row className="d-flex align-items-center mt-4">
              <Col lg="4">
                <label className="form-control-label">Is Termination Voluntary or Involuntary *</label>
              </Col>
              <Col lg="8">
                <div className="d-flex flex-column flex-wrap">
                  <div className="form-check form-check-inline mr-3">
                    <Input
                      className="form-check-input"
                      type="radio"
                      name="termination-category"
                      id="termination-category-involuntary"
                      value={'Involuntary'}
                      checked={terminationCategory === 'Involuntary'}
                      onChange={(e) => setTerminationCategory(e.target.value)}
                      required
                    />
                    <Label className="form-check-label mr-2" htmlFor="termination-category-involuntary">
                      Involuntary
                    </Label>
                  </div>
                  <div className="form-check form-check-inline mr-3">
                    <Input
                      className="form-check-input"
                      type="radio"
                      name="termination-category"
                      id="termination-category-voluntary"
                      value={'Voluntary'}
                      checked={terminationCategory === 'Voluntary'}
                      onChange={(e) => setTerminationCategory(e.target.value)}
                      required
                    />
                    <Label className="form-check-label mr-2" htmlFor="termination-category-voluntary">
                      Voluntary
                    </Label>
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="d-flex align-items-center mt-4">
              <Col lg="4">
                <label className="form-control-label">Is Employee Rehirable? (Y/N) *</label>
              </Col>
              <Col lg="8">
                <div className="d-flex flex-column flex-wrap">
                  <div className="form-check form-check-inline mr-3">
                    <Input
                      className="form-check-input"
                      type="radio"
                      name="will-be-rehired"
                      id="will-be-rehired-yes"
                      checked={willBeRehired === true}
                      onChange={() => setWillBeRehired(true)}
                      required
                    />
                    <Label className="form-check-label mr-2" htmlFor="will-be-rehired-yes">
                      Yes
                    </Label>
                  </div>
                  <div className="form-check form-check-inline mr-3">
                    <Input
                      className="form-check-input"
                      type="radio"
                      name="will-be-rehired"
                      id="will-be-rehired-no"
                      checked={willBeRehired === false}
                      onChange={() => setWillBeRehired(false)}
                      required
                    />
                    <Label className="form-check-label mr-2" htmlFor="will-be-rehired-no">
                      No
                    </Label>
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="d-flex align-items-center mt-4">
              <Col lg="4">
                <label className="form-control-label">Reason: Termination *</label>
              </Col>
              <Col lg="8">
                <Input
                  className="form-input"
                  type="textarea"
                  value={terminationReason}
                  onChange={(e) => setTerminationReason(e.target.value)}
                  required
                />
              </Col>
            </Row>

            <Row className="d-flex align-items-center mt-4">
              <Col lg="4">
                <label className="form-control-label">What is the employee's non-work email address? *</label>
              </Col>
              <Col lg="8">
                <Input
                  className="form-input"
                  type="text"
                  value={terminationUserEmail}
                  onChange={(e) => setTerminationUserEmail(e.target.value)}
                  required
                />
              </Col>
            </Row>

            <hr />

            <h5 className="mt-4">TERMINATION REQUEST</h5>
            <p className="text-muted">Prior to terminate an employee, use this to grant permission.</p>

            <Row className="d-flex align-items-center mt-4">
              <Col lg="4">
                <label className="form-control-label">Classroom Observation *</label>
                <p className="text-muted small">Have any administrator(s) observe this employee in the classroom?</p>
              </Col>
              <Col lg="8">
                <Input
                  className="form-input"
                  type="textarea"
                  value={classroomObservation}
                  onChange={(e) => setClassroomObservation(e.target.value)}
                  required
                />
              </Col>
            </Row>

            <Row className="d-flex align-items-center mt-4">
              <Col lg="4">
                <label className="form-control-label">30 Day Check In *</label>
                <p className="text-muted small">Was 30 day check in with an education director completed?</p>
              </Col>
              <Col lg="8">
                <Input
                  className="form-input"
                  type="textarea"
                  value={thirtyDayCheckIn}
                  onChange={(e) => setThirtyDayCheckIn(e.target.value)}
                  required
                />
              </Col>
            </Row>

            <Row className="d-flex align-items-center mt-4">
              <Col lg="12">
                <PreloadRecordsTable title="One on One *" records={oneOnOnesRecords} />
                <p className="text-muted small mt-1">
                  How many one on one(s) have you had with this employee? List number and what policies were highlighted
                </p>
              </Col>
            </Row>

            <Row className="d-flex align-items-center mt-4">
              <Col lg="12">
                <PreloadRecordsTable title="Feedback *" records={feedbacksRecords} />
                <p className="text-muted small mt-1">
                  How many feedback(s) and conversations have you had with this employee? List number and what policies
                  were highlighted
                </p>
              </Col>
            </Row>

            <Row className="d-flex align-items-center mt-4">
              <Col lg="4">
                <label className="form-control-label">Improvement: Feedback *</label>
                <p className="text-muted small">Did you seen any improvement after the feedback(s)?</p>
              </Col>
              <Col lg="8">
                <Input
                  className="form-input"
                  type="textarea"
                  value={feedbackImprovement}
                  onChange={(e) => setFeedbackImprovement(e.target.value)}
                  required
                />
              </Col>
            </Row>

            <Row className="d-flex align-items-center mt-4">
              <Col lg="12">
                <PreloadRecordsTable title="Performance Improvement Plan *" records={improvementsRecords} />
                <p className="text-muted small mt-1">
                  How many Performance Improvement Plans(s) have you had with this employee? List number and what
                  policies were highlighted
                </p>
              </Col>
            </Row>

            <Row className="d-flex align-items-center mt-4">
              <Col lg="4">
                <label className="form-control-label">Improvement: Performance Improvement Plan *</label>
                <p className="text-muted small">
                  Did you seen any improvement after the Performance Improvement Plan(s)?
                </p>
              </Col>
              <Col lg="8">
                <Input
                  className="form-input"
                  type="textarea"
                  value={performanceImprovementPlanImprovement}
                  onChange={(e) => setPerformanceImprovementPlanImprovement(e.target.value)}
                  required
                />
              </Col>
            </Row>

            <Row className="d-flex align-items-center mt-4">
              <Col lg="12">
                <PreloadRecordsTable title="Verbal Warning *" records={verbalWarningsRecords} />
                <p className="text-muted small mt-1">How many Verbal Warning(s) have you had with this employee?</p>
              </Col>
            </Row>

            <Row className="d-flex align-items-center mt-4">
              <Col lg="4">
                <label className="form-control-label">Improvement: Verbal Warning *</label>
                <p className="text-muted small">Did you seen any improvement after the Verbal Warning(s)?</p>
              </Col>
              <Col lg="8">
                <Input
                  className="form-input"
                  type="textarea"
                  value={verbalWarningImprovement}
                  onChange={(e) => setVerbalWarningImprovement(e.target.value)}
                  required
                />
              </Col>
            </Row>

            <Row className="d-flex align-items-center mt-4">
              <Col lg="12">
                <PreloadRecordsTable title="Disciplinary Action Notice *" records={disciplinaryActionsRecords} />
                <p className="text-muted small mt-1">
                  How many Disciplinary Action Notice(s) have you had with this employee? List number and what policies
                  were violated
                </p>
              </Col>
            </Row>

            <Row className="d-flex align-items-center mt-4">
              <Col lg="4">
                <label className="form-control-label">Improvement: Disciplinary Action Notice *</label>
                <p className="text-muted small">
                  Did you seen any improvement after the Disciplinary Action Notice(s)?
                </p>
              </Col>
              <Col lg="8">
                <Input
                  className="form-input"
                  type="textarea"
                  value={disciplinaryActionNoticeImprovement}
                  onChange={(e) => setDisciplinaryActionNoticeImprovement(e.target.value)}
                  required
                />
              </Col>
            </Row>

            <Row className="d-flex align-items-center mt-4">
              <Col lg="4">
                <label className="form-control-label">Back Up Plan *</label>
                <p className="text-muted small">Do you have a back-up plan for this classroom?</p>
              </Col>
              <Col lg="8">
                <Input
                  className="form-input"
                  type="textarea"
                  value={backUpPlan}
                  onChange={(e) => setBackUpPlan(e.target.value)}
                  required
                />
              </Col>
            </Row>

            <DirectReportsReassignment
              employeeFirstName={employee?.user?.firstName || 'This employee'}
              directReports={directReports}
              supervisors={supervisors}
              value={newSupervisorId}
              onChange={setNewSupervisorId}
            />

            <div className="d-flex justify-content-end mt-4">
              <div>
                <Button className="btn-secondary" onClick={toggle} disabled={loading}>
                  Cancel
                </Button>
                <Button color="primary" type="submit" disabled={loading}>
                  {loading ? 'Submitting...' : 'Submit'}
                </Button>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </Modal>
  );
};

export default TerminatedEmployeeFormV2;
